import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AUTH_BG =
  "https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      login(data.token, data.user);
      if (data.user.must_change_password) {
        toast.message("Please set a new password to continue");
        navigate("/change-password");
      } else {
        toast.success(`Welcome back, ${data.user.name}`);
        navigate(data.user.role === "admin" ? "/admin" : "/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white flex items-center justify-center rounded-sm">
              <img src="/logo.png" alt="Caring Hands" className="w-10 h-10 object-contain" />
            </div>
            <span className="font-display font-bold text-xl tracking-tighter">Caring Hands</span>
          </div>
          <div>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95]">
              Time.<br />Location.<br />Pay.
            </h1>
            <p className="mt-6 text-white/80 max-w-sm text-sm leading-relaxed">
              Concierge Nursing Service · Contractor Portal. GPS-verified clock in / out, automatic payroll, and 1099-NEC tax forms — in one place.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <img src="/logo.png" alt="Caring Hands Concierge Nursing" className="h-20 w-20 object-contain mb-6" />
          <div className="label-caps mb-2">Sign in</div>
          <h2 className="font-display font-bold text-3xl tracking-tighter mb-8">
            Welcome back
          </h2>

          <div className="mb-4">
            <Label className="label-caps">Email</Label>
            <Input type="email" className="rounded-none mt-2 h-11" value={form.email} onChange={upd("email")} required data-testid="email-input" />
          </div>

          <div className="mb-4">
            <Label className="label-caps">Password</Label>
            <Input type="password" className="rounded-none mt-2 h-11" value={form.password} onChange={upd("password")} required data-testid="password-input" />
          </div>

          {error && (
            <div className="mb-4 text-destructive text-sm font-mono border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={busy} className="w-full rounded-none h-11 mt-2" data-testid="auth-submit-button">
            {busy ? "Please wait…" : "Sign in"}
          </Button>

          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="mt-4 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Accounts are created by your administrator. Contact them if you need access.
          </p>
        </form>
      </div>
    </div>
  );
}
